import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { FaLock, FaEnvelope, FaUser, FaBuilding, FaCreditCard, FaCalendarAlt } from 'react-icons/fa';
import PublicLayout from '../../components/PublicLayout';
import '../../styles/Auth.css';
import googleLogo from '../../assets/google-logo.svg';

export default function Signup() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    companyName: '',
    currentPlanName: 'Free',
    currentPlanType: 'Monthly'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const { signup } = useAuth();
  const navigate = useNavigate();

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      return 'Password must be at least 8 characters long';
    }
    if (!hasUpperCase) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!hasLowerCase) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!hasNumbers) {
      return 'Password must contain at least one number';
    }
    if (!hasSpecialChar) {
      return 'Password must contain at least one special character';
    }
    return null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // If plan is changed to Free, automatically set billing to Monthly
    if (name === 'currentPlanName' && value === 'Free') {
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        currentPlanType: 'Monthly' // Force Monthly for Free plan
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    // Password validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    
    setLoading(true);
    
    try {
      const { confirmPassword, ...signupData } = formData;
      if (await signup(signupData)) {
        // Redirect based on plan type
        if (formData.currentPlanName === 'Free') {
          navigate('/app');
        } else {
          // For Individual and Team plans, redirect to billing page
          navigate('/billing');
        }
      } else {
        setError('An error occurred during signup');
      }
    } catch (err) {
      setError(err.message || 'An error occurred during signup');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    // Google signup tracking will be handled by GTM
  };

  return (
    <PublicLayout>
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Create Your Account</h1>
            <p>Create your account today — no credit card required</p>
          </div>
          
          {/* Social Login Buttons */}
          <div className="social-login-container">
            {/* Google Signup Button */}
            <a
              href={process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api/auth/google` : '/api/auth/google'}
              className="google-login-btn"
              onClick={handleGoogleSignup}
            >
              <img src={googleLogo} alt="Google logo" className="google-logo" />
              Continue with Google
            </a>
          </div>

          <div className="divider">
            <span>or</span>
          </div>
          
          {error && (
            <div className="auth-error" style={{ position: 'relative' }}>
              <button 
                onClick={() => setError(null)}
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  background: 'none',
                  border: 'none',
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: '#dc2626',
                  padding: '0',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Close"
              >
                ×
              </button>
              <span style={{ paddingRight: '24px' }}>{error}</span>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <div className="input-with-icon">
                <FaUser className="input-icon" />
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  placeholder="Your first name"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
              
            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <div className="input-with-icon">
                <FaUser className="input-icon" />
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  placeholder="Your last name"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="companyName">Company Name</label>
              <div className="input-with-icon">
                <FaBuilding className="input-icon" />
                <input
                  id="companyName"
                  name="companyName"
                  type="text"
                  placeholder="Your Company name"
                  value={formData.companyName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <div className="input-with-icon">
                <FaEnvelope className="input-icon" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-with-icon">
                <FaLock className="input-icon" />
                <input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Create a strong password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="input-with-icon">
                <FaLock className="input-icon" />
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="currentPlanName">Plan</label>
              <div className="input-with-icon">
                <FaCreditCard className="input-icon" />
                <select
                  id="currentPlanName"
                  name="currentPlanName"
                  value={formData.currentPlanName}
                  onChange={handleChange}
                  required
                >
                  <option value="Free">Free Plan</option>
                  <option value="Individual">Individual Plan</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="currentPlanType">Billing Cycle</label>
              <div className="input-with-icon">
                <FaCalendarAlt className="input-icon" />
                <select
                  id="currentPlanType"
                  name="currentPlanType"
                  value={formData.currentPlanType}
                  onChange={handleChange}
                  required
                  disabled={formData.currentPlanName === 'Free'}
                >
                  <option value="Monthly">Monthly</option>
                  <option value="Annual" disabled={formData.currentPlanName === 'Free'}>
                    Annual (Save 20%){formData.currentPlanName === 'Free' ? ' - Not available for Free plan' : ''}
                  </option>
                </select>
              </div>
              {formData.currentPlanName === 'Free' && (
                <small style={{ color: '#64748b', fontSize: '12px' }}>
                  Free plan is set to Monthly billing by default
                </small>
              )}
            </div>

            <button 
              type="submit"
              className="auth-button"
              disabled={loading}
              aria-busy={loading}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>
          
          <div className="auth-footer">
            <p>Already have an account? <Link to="/login">Log In</Link></p>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
